import { useState, type FormEvent } from 'react';
import { contactInfo } from '@/mocks/homeData';

const FORM_ADDR = 'https://readdy.ai/api/form/dag7igbep053ijub25u0';

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('contact_alt') as HTMLInputElement)?.value ?? '';

    if (honeypot.trim()) {
      setStatus('success');
      setMessage('Thank you! Your message has been received.');
      return;
    }

    const data = new FormData(form);
    if (honeypot) {
      data.delete('contact_alt');
    }

    setStatus('loading');
    try {
      const response = await fetch(FORM_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();

      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg =
        (meta?.message as string) ||
        (parsed?.message as string) ||
        (meta?.detail as string) ||
        responseText;

      const isSpam = /spam/i.test(serverMsg);
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        setMessage('Thank you! Your message has been received. We\u2019ll be in touch soon.');
        form.reset();
      } else {
        setStatus('error');
        setMessage(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Unable to send your message. Please try again.');
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-background-50">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-12 bg-primary-500/50"></span>
              <span className="text-primary-500">◆</span>
            </div>
            <span className="text-xs uppercase tracking-[0.3em] text-primary-500/90">Get in Touch</span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-foreground-50 mt-4">
              Contact Us
            </h2>

            <div className="mt-10 flex flex-col gap-7">
              <div className="flex gap-4 items-start">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-500 shrink-0">
                  <i className="ri-map-pin-line text-xl"></i>
                </div>
                <div>
                  <h4 className="font-heading text-lg font-semibold text-foreground-50">Location</h4>
                  <p className="mt-1 text-sm text-foreground-200/70 leading-relaxed">
                    {contactInfo.address}
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-500 shrink-0">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <div>
                  <h4 className="font-heading text-lg font-semibold text-foreground-50">Phone</h4>
                  <a
                    href={contactInfo.phoneHref}
                    className="mt-1 text-sm text-foreground-200/70 hover:text-primary-500 transition-colors"
                  >
                    {contactInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-500 shrink-0">
                  <i className="ri-time-line text-xl"></i>
                </div>
                <div>
                  <h4 className="font-heading text-lg font-semibold text-foreground-50">Hours</h4>
                  <div className="mt-1 flex flex-col gap-1 text-sm text-foreground-200/70">
                    {contactInfo.hours.map((h) => (
                      <span key={h.day}>
                        {h.day}: {h.time}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-background-100 border border-background-200/60 rounded-lg p-6 md:p-8">
            <form
              id="berties-contact"
              className="flex flex-col gap-5"
              onSubmit={handleSubmit}
              data-readdy-form="berties-contact"
            >
              <div className="spam-guard-field" aria-hidden="true">
                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="name" className="text-sm font-medium text-foreground-50">
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  required
                  className="bg-background-50 border border-background-200/60 rounded-md px-4 py-3 text-sm text-foreground-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="text-sm font-medium text-foreground-50">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="bg-background-50 border border-background-200/60 rounded-md px-4 py-3 text-sm text-foreground-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="phone" className="text-sm font-medium text-foreground-50">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="bg-background-50 border border-background-200/60 rounded-md px-4 py-3 text-sm text-foreground-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="message" className="text-sm font-medium text-foreground-50">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  required
                  maxLength={500}
                  className="bg-background-50 border border-background-200/60 rounded-md px-4 py-3 text-sm text-foreground-50 resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={status === 'loading'}
                className="whitespace-nowrap text-sm font-semibold px-6 py-3 rounded-md text-background-950 bg-primary-500 hover:bg-primary-400 transition-colors disabled:opacity-60 cursor-pointer"
              >
                {status === 'loading' ? 'Sending\u2026' : 'Send Message'}
              </button>

              {status === 'success' && (
                <p className="text-sm text-primary-500">{message}</p>
              )}
              {status === 'error' && (
                <p className="text-sm text-accent-500">{message}</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}